import { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { rsaEncrypt } from '@PetSureAustralia/petsure-payment/core';
import { ENTER_CREDIT_CARD, PCI_PAYMENT_ACTION, PAYMENT_METHOD } from '../../../constants/constants';
import { setInstalmentToPay, clearInstalmentToPay } from '../../../store/slice/paymentSlice';
import { SLICES } from '../../../constants/slices';
import { initInstalments, hasOneFailedInit, initWebCollectionErrorMessage } from '../../../helpers/paymentHelper';
import { BrandContext } from '../../../contextProviders/brandContext';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { SESSION_STORAGE_KEYS } from '../../../constants/sessionStorageKeys';
import { useLazyGetCustomerDetailQuery } from '../../../data/customer';
import {
  useInitiateWebCollectionMutation,
  usePremiumPaymentMutation,
  useInsertInstalmentRecordMutation
} from '../../../data/premiumsandpayments';

export const useOutstandingPaymentTable = (isInstalmentProcessingEnabled = false) => {
  const dispatch = useDispatch();
  const validatedOustandingDetails = useSelector((state) => state?.[SLICES.PAYMENT].validatedOustandingDetailsList);
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const auth0Token = authDetails?.auth0AccessToken;

  const [validInstalmentRecords, setValidInstalmentRecords] = useState([]);
  const [errorRows, setErrorRows] = useState({});
  const [totalAmount, setTotalAmount] = useState({});

  const [initWebCollection] = useInitiateWebCollectionMutation();
  const [updateInstalmentInUpm] = usePremiumPaymentMutation();
  const [insertInstalmentRecord] = useInsertInstalmentRecordMutation();
  const [initInstalmentsErrors, setInitInstalmentsErrors] = useState({});
  const [processingPolicyNumber, setProcessingPolicyNumber] = useState(null);
  const [getCustomerDetails] = useLazyGetCustomerDetailQuery();
  const PUBLIC_KEY = process.env.RSA_ENCRYPT_PUBLIC_KEY;

  const handleNextButton = (transaction) => {
    const activeStep = ENTER_CREDIT_CARD;
    dispatch(clearInstalmentToPay());
    dispatch(setInstalmentToPay({ activeStep, transaction }));
  };

  const handleSelect = (installmentIndex, petIndex) => {
    setValidInstalmentRecords((prevRows) => {
      // Clone the rows array
      const newRows = prevRows.map((pet, index) => {
        if (index !== petIndex) return pet; // Keep other pets unchanged

        // Clone the selected pet object
        const updatedPet = { ...pet, PremiumInstallment: [...pet.PremiumInstallment] };

        // Clone the installment list
        const premiumInstallments = updatedPet.PremiumInstallment.map((installment, i) => ({
          ...installment
        }));

        // Check if all previous installments are selected
        const canSelect = premiumInstallments.slice(0, installmentIndex).every((installment) => installment.isSelected);

        if (!canSelect) {
          setErrorRows((prevErrors) => ({
            ...prevErrors,
            [`${petIndex}-${installmentIndex}`]:
              'Please pay the amount that is most overdue first before you pay this one.'
          }));
          return updatedPet;
        }

        // Toggle `isSelected`
        premiumInstallments[installmentIndex].isSelected = !premiumInstallments[installmentIndex].isSelected;

        // If unchecking an installment, uncheck all subsequent installments
        if (!premiumInstallments[installmentIndex].isSelected) {
          premiumInstallments.forEach((installment, index) => {
            if (index > installmentIndex) {
              installment.isSelected = false;
            }
          });
        }

        // Remove error messages for subsequent installments
        setErrorRows((prevErrors) => {
          const newErrors = { ...prevErrors };

          premiumInstallments.forEach((_, index) => {
            if (index > installmentIndex) {
              delete newErrors[`${petIndex}-${index}`];
            }
          });
          return newErrors;
        });

        // Update total amount
        const sumAmount = premiumInstallments.reduce(
          (total, installment) => (installment.isSelected ? total + installment.DueAmount : total),
          0
        );

        const roundedAmount = Number(sumAmount.toFixed(2));

        setTotalAmount((prevTotal) => ({
          ...prevTotal,
          [petIndex]: roundedAmount
        }));

        updatedPet.PremiumInstallment = premiumInstallments;
        return updatedPet;
      });

      return newRows;
    });
  };

  useEffect(() => {
    if (validatedOustandingDetails?.updatedTransactionsData) {
      setValidInstalmentRecords(validatedOustandingDetails.updatedTransactionsData);
    }
  }, [validatedOustandingDetails.updatedTransactionsData]);

  const {
    activeBrandContext: {
      brand: { partner, partnerPrefix },
      cspConfiguration: {
        creditCardConfig: {
          [process.env.REACT_APP_ENV]: { formUrl },
          paynowSupplierBusinessCode,
          paynowSupplierBusinessCodePetsure
        }
      }
    }
  } = useContext(BrandContext);

  const encryptUserStates = (initRequestIds, policyNumber, isPetsureUnderwriter) => {
    const instalmentObject = initRequestIds.map((item) => ({
      RequestId: item.RequestId,
      CollectedAmount: item.CollectedAmount,
      InstalmentNumber: item.InstalmentNumber
    }));

    const userStateObj = JSON.stringify({
      DigitalToken: sessionStorage?.getItem(SESSION_STORAGE_KEYS.CSP_ACCESS_TOKEN),
      PartnerNumber: partner,
      PolicyNumber: policyNumber,
      PaymentMethod: PAYMENT_METHOD.CREDIT_CARD,
      UsePetsure: isPetsureUnderwriter
    });

    const instalmentStateObj = JSON.stringify({
      OutstandingPremiums: instalmentObject
    });

    const encryptedUserStateObj = rsaEncrypt(userStateObj, PUBLIC_KEY);
    const encryptedInstalmentStateObj = rsaEncrypt(instalmentStateObj, PUBLIC_KEY);
    const userState = encodeURIComponent(encryptedUserStateObj);
    const instalmentState = encodeURIComponent(encryptedInstalmentStateObj);

    return { userState, instalmentState };
  };

  const generateUrl = (userState, instalmentState, action) => {
    return `${window.location.origin}${CSPUrlsMap.payNow.url}?userState=${userState}&instalmentState=${instalmentState}&action=${action}`;
  };

  const onBeforePaymentSubmit = async () => {
    // This api call is only to extend DAPI token expiry time before redirecting to payment gateway.
    await getCustomerDetails(auth0Token).unwrap();
    return true;
  };

  const generatePaymentReferenceNumber = (instalmentToPay) => async () => {
    setProcessingPolicyNumber(instalmentToPay.PolicyNumber);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.INIT_REQUEST_IDS);
    const initRequestIds = await initInstalments(instalmentToPay?.PremiumInstallment, initWebCollection);
    const hasZeroRequestId = initRequestIds.find((item) => item.RequestId === 0);
    if (hasZeroRequestId) {
      const errorMessage = initWebCollectionErrorMessage(hasZeroRequestId.ErrorMessage);

      setInitInstalmentsErrors((prev) => ({
        ...prev,
        [instalmentToPay.PolicyNumber]: errorMessage
      }));
      hasOneFailedInit(initRequestIds, updateInstalmentInUpm);
      return null;
    }
    sessionStorage.setItem(SESSION_STORAGE_KEYS.INIT_REQUEST_IDS, JSON.stringify(initRequestIds));

    if (!isInstalmentProcessingEnabled) return null;

    const insertInstalmentPayload = {
      PolicyNumber: instalmentToPay.PolicyNumber,
      PartnerCode: partnerPrefix,
      Instalments: initRequestIds
    };

    const insertInstalmentRequest = await insertInstalmentRecord(insertInstalmentPayload);
    const paymentReferenceNumber = insertInstalmentRequest?.data?.responseObject.ReferenceNumber || null;
    sessionStorage.setItem(SESSION_STORAGE_KEYS.PAYMENT_REFERENCE_NUMBER, paymentReferenceNumber);
    return paymentReferenceNumber;
  };

  const createReturnUrl = (instalmentToPay) => async () => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEYS.INIT_REQUEST_IDS);

    const initRequestIds = stored ? JSON.parse(stored) : [];

    if (!Array.isArray(initRequestIds) || !initRequestIds.length) {
      return null;
    }
    const { userState, instalmentState } = encryptUserStates(
      initRequestIds,
      instalmentToPay.PolicyNumber,
      instalmentToPay.IsPetSureUnderwriter
    );

    return generateUrl(userState, instalmentState, PCI_PAYMENT_ACTION.PROCESSED);
  };

  const cancelUrl = (instalmentToPay) => async () => {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEYS.INIT_REQUEST_IDS);

    const initRequestIds = stored ? JSON.parse(stored) : [];

    if (!Array.isArray(initRequestIds) || !initRequestIds.length) {
      return null;
    }

    const { userState, instalmentState } = encryptUserStates(
      initRequestIds,
      instalmentToPay.PolicyNumber,
      instalmentToPay.IsPetSureUnderwriter
    );

    return generateUrl(userState, instalmentState, PCI_PAYMENT_ACTION.CANCELLED);
  };

  const supplierBusinessCode = (isPetsureUnderwriter) => {
    return isPetsureUnderwriter ? paynowSupplierBusinessCodePetsure : paynowSupplierBusinessCode;
  };

  const isQuickWebButtonDisabled = (totalAmount) => {
    return !totalAmount || totalAmount <= 0;
  };

  return {
    validInstalmentRecords,
    errorRows,
    totalAmount,
    handleSelect,
    handleNextButton,
    initInstalmentsErrors,
    createReturnUrl,
    cancelUrl,
    supplierBusinessCode,
    isQuickWebButtonDisabled,
    formUrl,
    partner,
    onBeforePaymentSubmit,
    partnerPrefix,
    generatePaymentReferenceNumber,
    processingPolicyNumber
  };
};
